import { Add } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AddEditLead, CrmDashboard, CrmSidebar, LeadList, LeadTaskOverview } from "../../components/crm-pro";
import { PageHeader } from "../../components/v3";
import {
  CRM_PRO_CREATE_LEAD,
  CRM_PRO_CREATE_TASK,
  CRM_PRO_DELETE_TASK,
  CRM_PRO_EDIT_TASK,
  CRM_PRO_EXPORT_LEAD,
  CRM_PRO_UPDATE_TASK_PROGRESS,
  CRM_PRO_VIEW_ALL_LEAD,
  CRM_PRO_VIEW_ASSIGNED_LEAD,
} from "../../constants";
import { PageLayout } from "../../layouts/PageLayout";
import { useHasPermission } from "../../utils";
import { LeadService } from "../../services";
import { usePersistedPageValue } from "../../hooks/usePersistedTableState";
import { CRM_PRO_ROUTES } from "./constants";

const ALL_LEADS_STATUS = { statusId: null, statusName: "All Leads" };

const isLeadStatus = (value: unknown): value is { statusId: string | number | null; statusName: string } =>
  typeof value === "object" &&
  value !== null &&
  "statusId" in value &&
  "statusName" in value &&
  typeof value.statusName === "string";

export const LeadsAndDashboardPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const tabFromPath = location.pathname === CRM_PRO_ROUTES.leads ? "leads" : "dashboard";

  const [totalCount, setTotalCount] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [reload, setReload] = useState(0);
  // The sidebar status filter is remembered with the table: Back restores it, forward navigation starts from all leads.
  const [leadStatus, setLeadStatus] = usePersistedPageValue("lead-status", ALL_LEADS_STATUS, isLeadStatus);
  const { statusId: selectedStatus, statusName } = leadStatus;
  const [leadTasks, setLeadTasks] = useState([]);

  const [activeTab, setActiveTab] = useState(tabFromPath);

  useEffect(() => {
    setActiveTab(tabFromPath);
  }, [tabFromPath]);

  const hasViewAllLead = useHasPermission(CRM_PRO_VIEW_ALL_LEAD);
  const hasViewAssignedLead = useHasPermission(CRM_PRO_VIEW_ASSIGNED_LEAD);
  const hasCreateLead = useHasPermission(CRM_PRO_CREATE_LEAD);
  const hasExportLead = useHasPermission(CRM_PRO_EXPORT_LEAD);
  const hasCreateTask = useHasPermission(CRM_PRO_CREATE_TASK);
  const hasEditTask = useHasPermission(CRM_PRO_EDIT_TASK);
  const hasDeleteTask = useHasPermission(CRM_PRO_DELETE_TASK);
  const hasUpdateTaskProgress = useHasPermission(CRM_PRO_UPDATE_TASK_PROGRESS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // TODO: getAllLeadTasks() caps at limit: 1000 — sidebar counts under-report past 1000 tasks.
        const tasks = await LeadService.getAllLeadTasks();
        if (!cancelled) setLeadTasks(tasks);
      } catch (error) {
        console.error("Failed to fetch lead tasks", error);
        if (!cancelled) setLeadTasks([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const statusIdNum = selectedStatus ? Number(selectedStatus) : null;
  const filteredTasks = useMemo(
    () => (statusIdNum ? leadTasks.filter((t) => Number(t.leadStatusId) === statusIdNum) : leadTasks),
    [leadTasks, statusIdNum],
  );

  const taskLeadCounts = useMemo(() => {
    const leadSetByStatus: Record<number, Set<number>> = {};
    for (const t of leadTasks) {
      // Completed tasks do not contribute to the sidebar pipeline count.
      if (t?.status === "DONE" || t?.isCompleted) continue;
      const sid = Number(t.leadStatusId);
      const lid = Number(t.leadId);
      if (sid && lid) {
        if (!leadSetByStatus[sid]) leadSetByStatus[sid] = new Set();
        leadSetByStatus[sid].add(lid);
      }
    }
    const counts: Record<number, number> = {};
    for (const [sid, leads] of Object.entries(leadSetByStatus)) {
      counts[Number(sid)] = leads.size;
    }
    return counts;
  }, [leadTasks]);

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleReload = () => setReload((prev) => prev + 1);

  const handleStatusClick = (statusDetails) => {
    // A null payload means the active status was toggled off — reset to "All Leads".
    setLeadStatus({
      statusId: statusDetails?.statusId ?? null,
      statusName: statusDetails?.statusName ?? ALL_LEADS_STATUS.statusName,
    });
  };

  // Check if the user has permission to create leads
  const buttons = [
    hasCreateLead &&
      activeTab === "leads" && {
        buttonText: "Add Lead",
        buttonIcon: Add,
        onClick: handleOpenModal,
      },
  ];

  const chipItems = [
    {
      label: "Dashboard",
      onClick: () => {
        navigate(CRM_PRO_ROUTES.dashboard);
        setActiveTab("dashboard");
      },
    },
    {
      label: "Leads",
      onClick: () => {
        navigate(CRM_PRO_ROUTES.leads);
        setActiveTab("leads");
      },
    },
  ];

  const renderActiveTab = () => {
    switch (activeTab) {
      case "dashboard":
        return <CrmDashboard />;
      case "leads": {
        return (
          <Box sx={{ mt: -7, zIndex: 2, position: "relative" }}>
            {(hasCreateTask || hasEditTask || hasDeleteTask || hasUpdateTaskProgress) && (
              <LeadTaskOverview tasks={filteredTasks} />
            )}

            {(hasViewAllLead || hasViewAssignedLead) && (
              <LeadList
                leadFilter={selectedStatus}
                filteredTasks={filteredTasks}
                totalCount={totalCount}
                setTotalCount={setTotalCount}
                onSuccess={handleReload}
                reloadData={reload}
                showExportAction={hasExportLead}
              />
            )}
          </Box>
        );
      }
      default:
        return (
          <Box sx={{ mt: -7, zIndex: 2, position: "relative" }}>
            <CrmDashboard />
          </Box>
        );
    }
  };

  return (
    <>
      <PageLayout>
        <Box sx={{ display: "flex" }}>
          {/* crm sidebar */}
          {activeTab === "leads" && (
            <CrmSidebar
              onStatusClick={handleStatusClick}
              reloadData={reload}
              taskLeadCounts={taskLeadCounts}
              selectedStatusId={selectedStatus}
            />
          )}
          <Box component="main" sx={{ flexGrow: 1, position: "relative", minWidth: 0, width: "100%" }}>
            {/* header section */}
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <PageHeader
                title={activeTab === "dashboard" ? "Dashboard" : `${statusName} (${totalCount})`}
                buttons={buttons}
                chipItems={chipItems}
                activeTab={activeTab}
              />
            </Box>

            {renderActiveTab()}
          </Box>
        </Box>
      </PageLayout>

      {/* Add Lead modal*/}
      <AddEditLead open={openModal} onClose={handleCloseModal} onSuccess={handleReload} />
    </>
  );
};
